/**
 * Conversations / Messages / Chat API - Phase 2.6 SSE 流式对话.
 *
 * 后端端点 (apps/backend/app/api/v1/conversations.py):
 *   GET    /conversations?project_id=xxx&starred=true  -> Conversation[]
 *   POST   /conversations  { role_id, project_id?, title? } -> Conversation
 *   POST   /conversations/{id}/star                    -> Conversation (starred 翻转)
 *   GET    /conversations/{id}/messages?limit=200      -> Message[]
 *   DELETE /conversations/{id}                          -> 204
 *   POST   /conversations/{id}/chat  { role_id, content } -> SSE text/event-stream
 *   POST   /conversations/{id}/opening { role_id }      -> SSE text/event-stream (角色开场白)
 *
 * SSE 流式: axios 不支持流式响应, 改用原生 fetch + ReadableStream 逐帧解析.
 *   - 仍带 Bearer 头 (从 getAccessToken 取).
 *   - 调用方通过 AbortSignal 可中断生成.
 */
import { http, getAccessToken, refreshAccessToken, clearTokens, redirectToLogin, apiBaseUrl } from './client'
import { showNotify } from '@/stores/notify'
import type {
  ChatRequest,
  Conversation,
  ConversationType,
  Message,
  Role,
  SSEEvent,
  UUID,
} from '@/types/api'

export interface CreateConversationRequest {
  role_id?: Role['id'] | null
  project_id?: UUID | null
  title?: string
  type?: ConversationType
}

/** GET /conversations?project_id=xxx&starred=true&type=yyy -> Conversation[]
 *  starred=true 时仅返回收藏对话 (软删排除, 按 last_message_at desc).
 *  type 指定时按会话类型过滤 (chat/studio_image/studio_video/studio_music). */
export async function listConversations(
  projectId?: UUID,
  starred?: boolean,
  type?: ConversationType,
): Promise<Conversation[]> {
  const params: Record<string, unknown> = {}
  if (projectId) params.project_id = projectId
  if (starred) params.starred = true
  if (type) params.type = type
  const res = await http.get<Conversation[]>('/conversations', {
    params: Object.keys(params).length ? params : undefined,
  })
  return res.data
}

/** POST /conversations/{id}/star -> Conversation
 *  收藏到指定分组 (starred=true), 或取消收藏 (starred=false).
 *  body: { starred, star_group_id? } */
export async function toggleConversationStar(
  conversationId: UUID,
  starGroupId?: UUID | null,
): Promise<Conversation> {
  const res = await http.post<Conversation>(`/conversations/${conversationId}/star`, {
    starred: true,
    star_group_id: starGroupId ?? null,
  })
  return res.data
}

/** POST /conversations/{id}/star 取消收藏. */
export async function unstarConversation(conversationId: UUID): Promise<Conversation> {
  const res = await http.post<Conversation>(`/conversations/${conversationId}/star`, {
    starred: false,
  })
  return res.data
}

/** PATCH /conversations/{id}/rename -> Conversation (重命名). */
export async function renameConversation(
  conversationId: UUID,
  title: string,
): Promise<Conversation> {
  const res = await http.patch<Conversation>(`/conversations/${conversationId}/rename`, {
    title,
  })
  return res.data
}

/** POST /conversations -> Conversation */
export async function createConversation(
  payload: CreateConversationRequest,
): Promise<Conversation> {
  const res = await http.post<Conversation>('/conversations', payload)
  return res.data
}

/** GET /conversations/{id} -> Conversation */
export async function getConversation(conversationId: UUID): Promise<Conversation> {
  const res = await http.get<Conversation>(`/conversations/${conversationId}`)
  return res.data
}

/** GET /conversations/{id}/messages?limit=200 -> Message[] */
export async function listMessages(conversationId: UUID): Promise<Message[]> {
  const res = await http.get<Message[]>(`/conversations/${conversationId}/messages`, {
    params: { limit: 200 },
  })
  return res.data
}

/** DELETE /conversations/{id} -> 204 */
export async function deleteConversation(conversationId: UUID): Promise<void> {
  await http.delete(`/conversations/${conversationId}`)
}

/** POST /conversations/{id}/messages/{msgId}/export-doc -> 导出 Word 下载信息 */
export async function exportMessageWord(
  conversationId: UUID,
  messageId: UUID,
): Promise<{ file_id: UUID; file_url: string; file_name: string; size: number }> {
  const res = await http.post<{
    file_id: UUID
    file_url: string
    file_name: string
    size: number
    message_id: UUID
  }>(`/conversations/${conversationId}/messages/${messageId}/export-doc`)
  return res.data
}

/**
 * 解析 SSE text/event-stream 响应, 逐帧产出 SSEEvent (共享给 streamChat / streamOpening).
 *
 * 帧格式: 以空行 (\n\n) 分隔, 每帧含 `event: <type>` 与 `data: <json>` 行;
 * 默认 event 名为 'message'. 无法 JSON 解析的帧被静默跳过.
 * 由调用方先完成 HTTP 状态校验 (response.ok), 本函数只负责 body 读取与分帧.
 */
async function* parseSSEStream(response: Response): AsyncGenerator<SSEEvent> {
  if (!response.body) {
    throw new Error('响应无流式 body')
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })

    // SSE 事件以空行 (\n\n) 分隔
    let idx: number
    while ((idx = buffer.indexOf('\n\n')) !== -1) {
      const rawEvent = buffer.slice(0, idx)
      buffer = buffer.slice(idx + 2)

      let eventType = 'message'
      let dataStr = ''
      for (const line of rawEvent.split('\n')) {
        if (line.startsWith('event: ')) {
          eventType = line.slice(7).trim()
        } else if (line.startsWith('data: ')) {
          dataStr += line.slice(6)
        }
      }
      if (!dataStr) continue
      try {
        yield { event: eventType, data: JSON.parse(dataStr) as Record<string, unknown> }
      } catch {
        // 跳过无法解析的帧
      }
    }
  }
}

/**
 * 发送聊天消息并以 SSE 流式接收回复 (async generator).
 *
 * 用原生 fetch + ReadableStream 读 text/event-stream, 逐帧解析为 SSEEvent.
 * 调用方可通过 signal (AbortSignal) 中断生成.
 *
 * 后端事件:
 *   event: token     data: { seq, delta }
 *   event: usage     data: { token_input, token_output, cost }
 *   event: done      data: { message_id }
 *   event: error     data: { code, message }
 *   event: document  data: { file_id, file_url, file_name, size }
 */
/**
 * 带 401 自动刷新的 SSE fetch (供 streamChat / streamOpening 复用).
 * 原生 fetch 不走 axios 拦截器, 需手动处理 access_token 过期: 401 -> refresh -> 重试一次.
 */
async function fetchSSE(
  url: string,
  body: string,
  signal?: AbortSignal,
): Promise<Response> {
  const buildInit = (token: string | null): RequestInit => ({
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body,
    signal,
  })

  let token = getAccessToken()
  let response = await fetch(`${apiBaseUrl}${url}`, buildInit(token))

  if (!response.ok && response.status === 401) {
    const errBody = await response.json().catch(() => ({} as { code?: string }))
    if (errBody.code === 'SESSION_CONFLICT') {
      clearTokens()
      showNotify({
        title: '下线提醒',
        message: '您的账号已在其他设备登录, 您已被迫下线。如非本人操作, 请及时修改密码。',
        confirmText: '重新登录',
        variant: 'danger',
        onConfirm: () => {
          redirectToLogin()
        },
      })
      throw new Error('SESSION_CONFLICT')
    }
    token = await refreshAccessToken()
    response = await fetch(`${apiBaseUrl}${url}`, buildInit(token))
  }

  if (!response.ok) {
    const err = (await response.json().catch(() => ({
      message: `HTTP ${response.status}`,
    }))) as { message?: string; code?: string }
    const e = new Error(err.message || `HTTP ${response.status}`) as Error & { code?: string }
    if (err.code) e.code = err.code
    throw e
  }

  return response
}

export async function* streamChat(
  conversationId: UUID,
  payload: ChatRequest,
  signal?: AbortSignal,
): AsyncGenerator<SSEEvent> {
  const response = await fetchSSE(
    `/conversations/${conversationId}/chat`,
    JSON.stringify(payload),
    signal,
  )
  yield* parseSSEStream(response)
}

/**
 * 拉取角色开场白并以 SSE 流式接收 (async generator).
 *
 * 进入空对话且已绑定 role_id 时由 Conversation 视图自动触发一次: 后端注入角色
 * prompt 生成开场问候, 前端只消费 SSE 流 (复用打字机/流式气泡), 不构造用户消息,
 * 也不接触 prompt 文本本身 (prompt 完全在后端侧注入).
 *
 * POST /conversations/{id}/opening { role_id } -> SSE text/event-stream
 * 事件帧与 /chat 完全一致: token {seq,delta} / usage {...} / done {message_id}
 * / document {file_id,file_url,file_name,size} / error {code,message}.
 * 其中 error.code === 'NO_PROMPT' 表示该角色暂无已发布 prompt,
 * 调用方应静默回退到问候空状态 (不展示错误条).
 */
export async function* streamOpening(
  conversationId: UUID,
  roleId: UUID,
  signal?: AbortSignal,
): AsyncGenerator<SSEEvent> {
  const response = await fetchSSE(
    `/conversations/${conversationId}/opening`,
    JSON.stringify({ role_id: roleId }),
    signal,
  )
  yield* parseSSEStream(response)
}
